import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router";
import { Badge, Box, Chip, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useAuth } from "src/context/AuthContext";
import { useCurrentPerson } from "src/utils/useCurrentPerson";
import { useT, useTRich } from "src/i18n";
import { listSupportMine } from "src/utils/tuki/tukiApi";
import { captureError } from "src/utils/errorReporting";
import { API_URL } from "src/utils/apiUrl";
import TilaaBackdrop from "src/components/quote/TilaaBackdrop";
import StepHeader from "src/components/quote/StepHeader";
import FrostedCard from "src/components/quote/FrostedCard";
import MessageThreadDialog from "src/components/common/MessageThreadDialog";

export default function Tuki() {
    const { token, logout } = useAuth();
    const person = useCurrentPerson();
    const t = useT();
    const tRich = useTRich();

    const [status, setStatus] = useState("open");
    const [threads, setThreads] = useState(null); // null = loading
    const [error, setError] = useState(null);
    const [openThreadId, setOpenThreadId] = useState(null);

    async function fetchThreads() {
        setThreads(null);
        setError(null);
        try {
            const items = await listSupportMine({ token, status });
            setThreads(items);
        } catch (err) {
            if (err?.status === 401) { logout(); return; }
            captureError(err, { tags: { entity: "messageThread", operation: "support_mine" } });
            setError(err);
            setThreads([]);
        }
    }

    useEffect(() => {
        document.title = t("provider.tuki.pageTitle");
        return () => { document.title = t("provider.tuki.pageTitleFallback"); };
    }, [t]);

    useEffect(() => {
        async function run() { await fetchThreads(); }
        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, token]);

    const openThread = threads?.find((th) => th.threadId === openThreadId) ?? null;
    const dialogTitle = openThread?.contextType === "pumppuRequest"
        ? t("provider.tuki.dialogTitleOffer", { id: openThread.contextId })
        : t("provider.tuki.dialogTitleGeneric");

    return (
        <TilaaBackdrop>
            <StepHeader>{t("provider.tuki.heading")}</StepHeader>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
                {t("provider.tuki.intro")}
            </Typography>

            <ToggleButtonGroup
                value={status}
                exclusive
                onChange={(_e, val) => { if (val) setStatus(val); }}
                size="small"
                sx={{ mb: 3 }}
            >
                <ToggleButton value="open">{t("provider.tuki.tabOpen")}</ToggleButton>
                <ToggleButton value="resolved">{t("provider.tuki.tabResolved")}</ToggleButton>
                <ToggleButton value="all">{t("provider.tuki.tabAll")}</ToggleButton>
            </ToggleButtonGroup>

            <Stack spacing={2}>
                {threads === null && (
                    <Typography variant="body2">{t("common.loading")}</Typography>
                )}
                {error && (
                    <Typography variant="body2" color="error">
                        {t("provider.tuki.loadFailed")}
                    </Typography>
                )}
                {threads !== null && threads.length === 0 && !error && (
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {t("provider.tuki.emptyNotice")}
                        {status === "open" && (
                            <Box component="span" sx={{ display: "block", mt: 0.5 }}>
                                {tRich("provider.tuki.emptyOpenHint", {
                                    link: (
                                        <Typography
                                            component="a"
                                            href="mailto:info@betonijerry.fi"
                                            variant="body2"
                                            color="primary"
                                            sx={{ textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                                        >
                                            {t("provider.tuki.emptyOpenHintLink")}
                                        </Typography>
                                    ),
                                })}
                            </Box>
                        )}
                    </Typography>
                )}
                {threads && threads.map((thread) => (
                    <FrostedCard
                        key={thread.threadId}
                        onClick={() => setOpenThreadId(thread.threadId)}
                        sx={{ p: 2, cursor: "pointer" }}
                    >
                        <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5 }}>
                            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                                {thread.contextType === "pumppuRequest"
                                    ? t("provider.tuki.cardTitleOffer", { id: thread.contextId })
                                    : t("provider.tuki.cardTitleGeneric", { id: thread.threadId })}
                            </Typography>
                            {thread.unreadCount > 0 && (
                                <Badge badgeContent={thread.unreadCount} color="error" max={99}>
                                    <Box sx={{ width: 16, height: 16 }} />
                                </Badge>
                            )}
                            <Chip
                                label={thread.status === "resolved" ? t("provider.tuki.statusResolved") : t("provider.tuki.statusOpen")}
                                color={thread.status === "resolved" ? "success" : "warning"}
                                size="small"
                            />
                        </Stack>
                        {thread.lastMessageBody && (
                            <Typography
                                variant="body2"
                                sx={{ color: "text.secondary",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    mb: 1,
                                }}
                            >
                                {thread.lastMessageBody}
                            </Typography>
                        )}
                        {thread.contextType === "pumppuRequest" && (
                            <Typography
                                component={RouterLink}
                                to={`/tarjouspyynnot/${thread.contextId}`}
                                variant="caption"
                                color="primary"
                                onClick={(e) => e.stopPropagation()}
                                sx={{ textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                            >
                                {t("provider.tuki.openOfferCta")}
                            </Typography>
                        )}
                    </FrostedCard>
                ))}
            </Stack>

            <MessageThreadDialog
                open={!!openThreadId}
                onClose={() => {
                    setOpenThreadId(null);
                    async function refetch() { await fetchThreads(); }
                    refetch();
                }}
                title={dialogTitle}
                threadId={openThreadId}
                currentPersonId={person?.personId ?? null}
                token={token}
                apiBaseUrl={API_URL}
            />
        </TilaaBackdrop>
    );
}
